import React from 'react';

import { useIntl } from 'react-intl-next';

import { type SearchResultPullRequestPartial } from '@atlassian/dynamic-cards-search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { removeMarkdown } from '../../../../../../../common/utils/remove-markdown';
import {
	enrichExcerpt,
	getIntegrationKey,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useBootstrap, usePrimaryProduct } from '../../../../../../../controllers/store';

import { messages } from './messages';

interface PullRequestSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultPullRequestPartial;
	position?: number;
}

export const PullRequestSearchResult = ({
	partial,
	position,
	icon,
}: PullRequestSearchResultProps) => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { url, permissionLevel, entity, excerpt } = partial;
	const { title, lastUpdate, container, pullRequestId } = entity || {};
	const label = formatMessage(messages.resultLabel);

	const lastModifiedComponent = lastUpdate ? <LastModified lastModified={lastUpdate} /> : null;
	const integrationKey = getIntegrationKey(partial.integrationId);

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getDescription = (excerpt?: string) => {
		const markdownRemoved = removeMarkdown(excerpt);
		return processExcerpt(markdownRemoved, true);
	};

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={getDescription(excerpt)}
			headerMetadata={
				<SearchMetadata
					product={integrationKey}
					productSelected={true}
					parts={[
						label,
						['pull-request-id', `#${pullRequestId}`],
						['repository-name', container?.name ?? null],
						['padlock', padlockIcon],
						['last-modified', lastModifiedComponent],
					]}
				/>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
